import React from 'react'
import { useTranslate } from 'src/localization/Trns'
import {
  ReceiptVerificationStatus,
  receiptVerificationStatusToTooltip,
} from 'src/models/receiptVerification.models'
import { css } from 'styled-components'

interface AttachmentVerificationTooltipProps {
  status?: ReceiptVerificationStatus
  reasons?: string[]
}

function isPositiveStatus(status?: ReceiptVerificationStatus): boolean {
  return (
    status === ReceiptVerificationStatus.Verified ||
    status === ReceiptVerificationStatus.AutoMatched
  )
}

function isNegativeStatus(status?: ReceiptVerificationStatus): boolean {
  return (
    status === ReceiptVerificationStatus.NotVerified ||
    status === ReceiptVerificationStatus.Mismatched
  )
}

function defaultReasonsForStatus(status?: ReceiptVerificationStatus): string[] {
  if (isPositiveStatus(status)) {
    return ['Verified amount', 'Verified date', 'Verified merchant']
  }
  if (isNegativeStatus(status)) {
    return [
      "Couldn't verify amount",
      "Couldn't verify date",
      "Couldn't verify merchant",
    ]
  }
  return []
}

function CheckGlyph(): React.ReactElement {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2.5 6.5L4.75 8.75L9.5 3.5"
        stroke="#10B981"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CrossGlyph(): React.ReactElement {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3 3L9 9M9 3L3 9"
        stroke="#EF4444"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

export default function AttachmentVerificationTooltip({
  status,
  reasons,
}: AttachmentVerificationTooltipProps): React.ReactElement {
  const translate = useTranslate()

  const effectiveReasons =
    reasons && reasons.length > 0 ? reasons : defaultReasonsForStatus(status)

  if (effectiveReasons.length === 0) {
    return <span>{translate(receiptVerificationStatusToTooltip(status))}</span>
  }

  const positive = isPositiveStatus(status)

  return (
    <ul
      css={css`
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 8px;
      `}
    >
      {effectiveReasons.map((reason, i) => (
        <li
          key={`${reason}-${i}`}
          css={css`
            display: flex;
            align-items: center;
            gap: 4px;
            font-size: 12px;
            line-height: 16px;
          `}
        >
          <span
            css={css`
              display: inline-flex;
              width: 16px;
              height: 16px;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            `}
            aria-hidden
          >
            {positive ? <CheckGlyph /> : <CrossGlyph />}
          </span>
          <span>{translate(reason)}</span>
        </li>
      ))}
    </ul>
  )
}
